import { useCallback } from 'react'
import { jsPDF } from 'jspdf'
import QRCode from 'qrcode'
import { toast } from 'sonner'
import posthog from 'posthog-js'
import { useLanguage } from '@/lib/i18n/language-provider'
import type { UseReportGeneratorParams } from './report/report-types'
import {
  PDF_LANDSCAPE_W, PDF_LANDSCAPE_H,
  svgToDataUrl,
} from './report/report-helpers'
import {
  collectAllElements, collectMires, collectProcessors,
  collectPanels, collectCabinets, collectCables, collectConnections,
} from './report/report-collectors'
import { drawCoverPage } from './report/report-cover'
import { drawTechnicalPages } from './report/report-technical'

export type { UseReportGeneratorParams } from './report/report-types'

export function useReportGenerator({
  projectName,
  cartouche,
  dataPages,
  powerPages,
  backupPorts,
  backupProcessors,
  resolveShareUrl,
  setIsExporting,
  setExportProgress,
  setShowExportSuccess,
  createExportSvgForDataPage,
  createExportSvgForPowerPage,
}: UseReportGeneratorParams) {
  const { t } = useLanguage()

  const generateReport = useCallback(async () => {
    try {
      setIsExporting(true)
      setExportProgress(t('synoptic.reportGenerating'))

      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })

      // ── Collect all data ──────────────────────────────────────────
      const allEl = collectAllElements(dataPages, powerPages)
      const mires = collectMires(dataPages)
      const processors = collectProcessors(allEl, backupProcessors)
      const panels = collectPanels(allEl, mires)
      const cabinets = collectCabinets(allEl)
      const cables = collectCables(dataPages, powerPages)
      const conns = collectConnections(dataPages, powerPages, backupPorts)

      // ── Computed values ───────────────────────────────────────────
      const totalPanels = mires.reduce((s, m) => s + m.totalPanels, 0) + allEl.filter(e => e.type === 'panel').length
      const totalProcessors = allEl.filter(e => e.type === 'processor').length
      const grandTotalPixels = mires.reduce((s, m) => s + m.totalPixels.width * m.totalPixels.height, 0)
      const procConsumption = processors.reduce((s, p) => s + (typeof p.powerConsumption === 'number' ? p.powerConsumption : 0), 0)
      const panelConsumption = panels.reduce((s, p) => s + p.powerConsumption * p.count, 0)
      const totalConsumption = procConsumption + panelConsumption

      // ── QR Code URL ───────────────────────────────────────────────
      // resolveShareUrl sauvegarde le projet et garantit un report_token :
      // le QR fonctionne même quand le projet reste privé. null → pas de QR.
      const shareUrl = await resolveShareUrl()
      let qrDataUrl: string | null = null
      if (shareUrl) {
        try {
          qrDataUrl = await QRCode.toDataURL(shareUrl, {
            width: 200, margin: 1,
            color: { dark: '#000000', light: '#ffffff' },
            errorCorrectionLevel: 'M',
          })
        } catch { /* skip */ }
      }

      // ════════════════════════════════════════════════════════════════
      // PAGE 1 : COVER
      // ════════════════════════════════════════════════════════════════
      await drawCoverPage(doc, {
        projectName, cartouche, t, qrDataUrl,
        mires, totalProcessors, totalPanels,
        grandTotalPixels, totalConsumption,
        totalCables: cables.total,
      })

      // ════════════════════════════════════════════════════════════════
      // SYNOPTIC PAGES (Data + Power) — landscape images
      // ════════════════════════════════════════════════════════════════
      const totalSynPages = dataPages.length + powerPages.length
      let counter = 0
      for (let i = 0; i < dataPages.length; i++) {
        counter++
        try {
          const svg = await createExportSvgForDataPage(dataPages[i], counter, totalSynPages)
          const img = await svgToDataUrl(svg)
          doc.addPage([PDF_LANDSCAPE_W, PDF_LANDSCAPE_H], 'landscape')
          doc.addImage(img, 'PNG', 0, 0, PDF_LANDSCAPE_W, PDF_LANDSCAPE_H)
        } catch { /* skip */ }
      }
      for (let i = 0; i < powerPages.length; i++) {
        counter++
        try {
          const svg = await createExportSvgForPowerPage(powerPages[i], counter, totalSynPages)
          const img = await svgToDataUrl(svg)
          doc.addPage([PDF_LANDSCAPE_W, PDF_LANDSCAPE_H], 'landscape')
          doc.addImage(img, 'PNG', 0, 0, PDF_LANDSCAPE_W, PDF_LANDSCAPE_H)
        } catch { /* skip */ }
      }

      // ════════════════════════════════════════════════════════════════
      // TECHNICAL DATA PAGES (each section = new page)
      // ════════════════════════════════════════════════════════════════
      drawTechnicalPages({
        doc, t, projectName, mires, processors, panels, cabinets, cables, conns,
        allElements: allEl, totalPanels, totalProcessors, grandTotalPixels,
        procConsumption, panelConsumption, totalConsumption,
        backupProcessors, backupPorts, dataPages,
      })

      // ════════════════════════════════════════════════════════════════
      // FOOTER on cover + technical pages (skip synoptic pages — they
      // already carry page numbering inside the SVG cartouche)
      // ════════════════════════════════════════════════════════════════
      const dateStr = new Date().toLocaleDateString()
      const pageCount = doc.getNumberOfPages()
      const synFirstPage = 2
      const synLastPage = 1 + totalSynPages
      for (let i = 1; i <= pageCount; i++) {
        if (i >= synFirstPage && i <= synLastPage) continue
        doc.setPage(i)
        const pw = doc.internal.pageSize.getWidth()
        const ph = doc.internal.pageSize.getHeight()
        doc.setFont('helvetica', 'normal')
        doc.setFontSize(6.5)
        doc.setTextColor(150)
        doc.text(
          `${t('report.generatedBy')} VisualGrid -- ${dateStr} -- ${t('report.page')} ${i}/${pageCount}`,
          pw / 2, ph - 8, { align: 'center' },
        )
      }

      // ── Save ─────────────────────────────────────────────────────
      const filename = `${(projectName || 'VisualGrid').replace(/[^a-zA-Z0-9_\- ]/g, '_')}_${t('report.technicalReport').replace(/\s/g, '_')}.pdf`
      doc.save(filename)

      setIsExporting(false)
      setShowExportSuccess(true)

      posthog.capture('synoptic:export_report', {
        data_pages_count: dataPages.length,
        power_pages_count: powerPages.length,
        has_qr: qrDataUrl !== null,
      })

      setTimeout(() => setShowExportSuccess(false), 2000)
    } catch {
      setIsExporting(false)
      toast.error(t('synoptic.reportExportError'))
    }
  }, [
    projectName, cartouche, dataPages, powerPages,
    backupPorts, backupProcessors, resolveShareUrl, t,
    setIsExporting, setExportProgress, setShowExportSuccess,
    createExportSvgForDataPage, createExportSvgForPowerPage,
  ])

  return { generateReport }
}
